import { useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { FileRow, type RowHandlers } from './FileRow.tsx';
import { Thumb } from './Thumb.tsx';
import { useUi } from '../../store/ui.ts';
import { IconChevronDown, IconChevronUp } from '../../lib/icons.tsx';
import { formatBytes, formatRelative } from '../../lib/format.ts';
import type { ApiNode, SortField } from '../../api/types.ts';

interface Props {
  namespaceId: string;
  items: ApiNode[];
  selected: Set<string>;
  renamingPath: string | null;
  onCancelRename: () => void;
  onToggleAll: (checked: boolean) => void;
  handlers: RowHandlers;
}

const COLUMNS: Array<{ field: SortField; label: string }> = [
  { field: 'name', label: '이름' },
  { field: 'size', label: '크기' },
  { field: 'modified', label: '수정일' },
];

const ROW_HEIGHT = 44;

export function FileTable({
  namespaceId, items, selected, renamingPath, onCancelRename, onToggleAll, handlers,
}: Props) {
  const view = useUi((s) => s.view);
  const sort = useUi((s) => s.sort);
  const order = useUi((s) => s.order);
  const setSort = useUi((s) => s.setSort);
  const scrollRef = useRef<HTMLDivElement>(null);

  // 1만 건 폴더에서도 첫 페인트가 빠르도록 화면에 보이는 행만 그린다
  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
  });

  if (view === 'grid') {
    return (
      <div className="table__scroll" ref={scrollRef}>
        <div className="grid">
          {items.map((node) => (
            <button
              key={node.id}
              className="gcard"
              data-selected={selected.has(node.path)}
              onClick={(e) => { handlers.onSelect(node, e); }}
              onDoubleClick={() => { handlers.onOpen(node); }}
              onContextMenu={(e) => { handlers.onContextMenu(node, e); }}
            >
              <div className="gcard__thumb">
                <Thumb namespaceId={namespaceId} node={node} variant="m" size={36} />
              </div>
              <div className="truncate" style={{ fontSize: 13 }}>{node.name}</div>
              <div className="faint" style={{ fontSize: 11 }}>
                {node.type === 'folder' ? '폴더' : formatBytes(node.size)}
                {' · '}{formatRelative(node.modified_at)}
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  const allSelected = items.length > 0 && items.every((i) => selected.has(i.path));

  return (
    <div className="table">
      <div className="table__head" role="row">
        <input
          className="checkbox" type="checkbox" checked={allSelected}
          onChange={(e) => { onToggleAll(e.target.checked); }}
          aria-label="전체 선택"
        />
        {COLUMNS.map((col) => (
          <button
            key={col.field}
            className="table__sort"
            onClick={() => { setSort(col.field); }}
            aria-label={`${col.label} 기준 정렬`}
          >
            {col.label}
            {sort === col.field && (order === 'asc'
              ? <IconChevronUp size={13} /> : <IconChevronDown size={13} />)}
          </button>
        ))}
        <span />
      </div>

      <div className="table__scroll" ref={scrollRef} role="grid" aria-label="파일 목록">
        <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
          {virtualizer.getVirtualItems().map((row) => {
            const node = items[row.index];
            if (!node) return null;
            return (
              <FileRow
                key={node.id}
                node={node}
                selected={selected.has(node.path)}
                renaming={renamingPath === node.path}
                onCancelRename={onCancelRename}
                handlers={handlers}
                style={{
                  position: 'absolute', top: 0, left: 0, right: 0,
                  transform: `translateY(${row.start}px)`,
                }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
